import { Component, Input, OnChanges, OnInit, OnDestroy, SimpleChanges } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { from, of, Subscription } from 'rxjs';
import { catchError, concatMap, filter, finalize, toArray } from 'rxjs/operators';
import { QueryService } from '../../comun/services/query.service';
import { UsuarioPerfil } from '../../comun/models/misdatos.model';
import { MisdatosService } from '../../comun/services/misdatos.service';
import { GlobalConstants } from '../../global-constants';
import Swal from 'sweetalert2';

interface FotoPrivada {
  ft_id: number;
  ft_titulo: string;
  ft_orden: number;
  ft_fechahora: string;
  thumb?: string;
  grande?: string;
  cargando?: boolean;
}
@Component({
  selector: 'app-fotos-privadas',
  templateUrl: './fotos-privadas.component.html',
  styleUrls: ['./fotos-privadas.component.css']
})
export class FotosPrivadasComponent implements OnInit, OnChanges, OnDestroy {
  // Id recibido del shortcode, igual que angular_mascotas.
  @Input() userid: number | string = 0;
  misdatos: UsuarioPerfil | null = null;
  private usuarioConectado = 0;
  fotos: FotoPrivada[] = [];
  cargando = false;
  subiendo = false;
  ordenando = false;
  arrastrando = false;
  error = '';
  filtro = '';
  seleccionada: FotoPrivada | null = null;
  archivos: File[] = [];
  vistaPrevia: { file: File; url: string }[] = [];
  private subs = new Subscription();
  private urls: string[] = [];
  private cargadoPara = 0;

  constructor(private query: QueryService, private http: HttpClient,
              private misdatosService: MisdatosService) {}

  get esPropietario(): boolean {
    return this.usuarioConectado > 0 && this.usuarioConectado === Number(this.userid);
  }
  get filtradas(): FotoPrivada[] {
    const q = this.filtro.trim().toLocaleLowerCase('es');
    return q ? this.fotos.filter(f => (f.ft_titulo || '').toLocaleLowerCase('es').includes(q)) : this.fotos;
  }
  ngOnInit(): void {
    this.getMisDatos();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userid'] && !changes['userid'].firstChange) {
      this.sincronizarGaleria();
    }
  }
  // Mismo patrón reactivo que perfil-publico: recibir misdatos$ del servicio
  // compartido, sin realizar otra petición GET ni crear otra fuente de datos.
  getMisDatos(): void {
    this.subs.add(this.misdatosService.misdatos$
      .pipe(filter((misdatos): misdatos is UsuarioPerfil => misdatos !== null))
      .subscribe((misdatos: UsuarioPerfil) => {
        this.misdatos = misdatos;
        this.usuarioConectado = Number(misdatos.ID) || 0;
        this.sincronizarGaleria();
      }));
  }
  private sincronizarGaleria(): void {
    if (!this.esPropietario) {
      this.limpiarMemoria();
      this.fotos = [];
      this.cargadoPara = 0;
      this.seleccionada = null;
      this.cancelarPendientes();
      return;
    }
    const id = this.usuarioConectado;
    if (id !== this.cargadoPara) {
      this.cargadoPara = id;
      this.cargarFotos();
    }
  }
  /** Mensajes controlados devueltos por PHP; nunca mostrar el texto técnico de HttpErrorResponse. */
  private mensajePHP(respuesta: any, alternativo: string): string {
    const leer = (valor: any, profundidad = 0): string => {
      if (profundidad > 4 || valor == null) return '';
      if (typeof valor === 'string') {
        try { return leer(JSON.parse(valor), profundidad + 1); } catch { return ''; }
      }
      if (typeof valor !== 'object') return '';
      if (typeof valor.mensaje === 'string' && valor.mensaje.trim()) return valor.mensaje.trim();
      // Angular HttpErrorResponse contiene error: Blob | string | objeto.
      if (valor.error && valor.error !== valor) return leer(valor.error, profundidad + 1);
      return '';
    };
    return leer(respuesta) || alternativo;
  }

  private errorAmigable(err: any, operacion: 'cargar' | 'subir' | 'editar' | 'eliminar' | 'ordenar' | 'imagen'): string {
    const mensajes = {
      cargar: 'No hemos podido cargar tus fotos. Inténtalo de nuevo.',
      subir: 'No se ha podido guardar la fotografía. Inténtalo de nuevo.',
      editar: 'No se ha podido guardar el título. Inténtalo de nuevo.',
      eliminar: 'No se ha podido eliminar la fotografía. Inténtalo de nuevo.',
      ordenar: 'No se ha podido guardar el orden de las fotografías. Inténtalo de nuevo.',
      imagen: 'No se ha podido mostrar una fotografía. Inténtalo de nuevo.'
    };
    const estado = Number(err?.status || 0);
    const detalle = this.mensajePHP(err, '');
    if (detalle) return detalle;
    if (estado === 401 || estado === 403) return 'Tu sesión ha caducado o no tienes permiso. Vuelve a iniciar sesión.';
    if (estado === 413) return 'La fotografía supera el tamaño permitido por el servidor. Prueba con una imagen más pequeña.';
    if (estado === 415) return 'El formato de la fotografía no es compatible. Utiliza JPG, PNG o WebP.';
    if (estado === 429) return 'Has realizado demasiadas solicitudes. Prueba de nuevo dentro de unos minutos.';
    if (estado === 0) return 'No se pudo conectar con el servidor. Comprueba tu conexión e inténtalo de nuevo.';
    return mensajes[operacion];
  }

  private headers(): HttpHeaders {
    const token = localStorage.getItem('token');
    return token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : new HttpHeaders();
  }
  private imagenUrl(id: number, mini: boolean): string {
    return `${GlobalConstants.urlServer}backend/api/privadas.php?foto=${id}&mini=${mini ? 1 : 0}`;
  }
  private cargarImagen(f: FotoPrivada, mini: boolean): void {
    this.subs.add(this.http.get(this.imagenUrl(f.ft_id, mini), {
      headers: this.headers(), responseType: 'blob'
    }).subscribe({
      next: blob => {
        if (!this.fotos.some(x => x.ft_id === f.ft_id)) return;
        const url = URL.createObjectURL(blob); this.urls.push(url);
        if (mini) f.thumb = url; else f.grande = url;
      },
      error: (err: any) => this.error = this.errorAmigable(err, 'imagen')
    }));
  }
  cargarFotos(preservarError = false): void {
    if (!this.esPropietario) return;
    this.cargando = true;
    if (!preservarError) this.error = '';
    this.subs.add(this.query.query('privadas', 'GET', null).subscribe({
      next: (r: any) => {
        if (r?.resultado !== 'OK') { this.error = this.mensajePHP(r, 'No se pudo cargar la galería.'); this.cargando = false; return; }
        this.limpiarMemoria();
        this.fotos = (r.registros || []).map((x: any) => ({
          ft_id: Number(x.ft_id), ft_titulo: String(x.ft_titulo || ''),
          ft_orden: Number(x.ft_orden), ft_fechahora: String(x.ft_fechahora || '')
        }));
        for (const f of this.fotos) this.cargarImagen(f, true);
        this.cargando = false;
      },
      error: (err: any) => {
        console.error('[iGuau / privadas GET]', {
          status: err?.status, url: err?.url, message: err?.message,
          respuesta: err?.error
        });
        this.error = this.errorAmigable(err, 'cargar');
        this.cargando = false;
      }
    }));
  }
  seleccionarArchivos(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) this.agregarArchivos(Array.from(input.files));
    input.value = '';
  }
  soltarArchivos(event: DragEvent): void {
    event.preventDefault(); this.arrastrando = false;
    if (event.dataTransfer?.files) this.agregarArchivos(Array.from(event.dataTransfer.files));
  }
  sobreArrastre(event: DragEvent): void { event.preventDefault(); this.arrastrando = true; }
  salirArrastre(event: DragEvent): void { event.preventDefault(); this.arrastrando = false; }
  private agregarArchivos(files: File[]): void {
    for (const file of files) {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 8 * 1024 * 1024) {
        this.error = 'Solo se admiten fotografías JPG, PNG o WebP de hasta 8 MB.'; continue;
      }
      const url = URL.createObjectURL(file); this.urls.push(url);
      this.vistaPrevia.push({ file, url }); this.archivos.push(file);
    }
  }
  quitarPendiente(i: number): void { this.archivos.splice(i, 1); this.vistaPrevia.splice(i, 1); }
  cancelarPendientes(): void { this.archivos=[]; this.vistaPrevia=[]; }
  subirFotos(): void {
    if (this.subiendo || !this.archivos.length || !this.esPropietario) return;
    this.subiendo = true; this.error = '';
    const fotos = [...this.archivos];
    this.subs.add(from(fotos).pipe(
      // Secuencial: ft_orden queda estable incluso al añadir varias fotografías.
      concatMap(file => {
        const data = new FormData();
        data.append('file', file, file.name); data.append('tipo', 'PR');
        data.append('tipo_id', '0'); data.append('principal', '');
        return this.http.post<any>(GlobalConstants.urlServer + 'backend/api/upload.php', data, {headers: this.headers()})
          .pipe(catchError((err: any) => {
            console.error('[iGuau / upload PR]', { status: err?.status, url: err?.url, respuesta: err?.error });
            return of({resultado: 'ERROR', mensaje: this.errorAmigable(err, 'subir')});
          }));
      }),
      toArray(),
      finalize(() => this.subiendo = false)
    ).subscribe(r => {
      const fallos = r.filter(x => x?.resultado !== 'OK');
      if (fallos.length) this.error = fallos.map(x => this.mensajePHP(x, 'No se pudo subir una fotografía')).join(' · ');
      else this.cancelarPendientes();
      // La recarga no debe borrar el error de PHP que acabamos de mostrar.
      this.cargarFotos(fallos.length > 0);
    }));
  }
  abrir(f: FotoPrivada): void {
    this.seleccionada = f;
    if (!f.grande) this.cargarImagen(f, false);
  }
  cerrar(): void { this.seleccionada = null; }
  async editarTitulo(f: FotoPrivada): Promise<void> {
    const resp = await Swal.fire({
      title: 'Título de la fotografía', input:'text', inputValue:f.ft_titulo,
      inputAttributes:{maxlength:'50'}, showCancelButton:true,
      confirmButtonText:'Guardar', cancelButtonText:'Cancelar',
      confirmButtonColor:'#527B69'
    });
    if (!resp.isConfirmed) return;
    const titulo = String(resp.value || '').trim();
    this.subs.add(this.query.query('privadas','RENOMBRAR',{ft_id:f.ft_id,ft_titulo:titulo}).subscribe({
      next: (r:any) => {if(r?.resultado==='OK') f.ft_titulo=titulo; else this.error=this.mensajePHP(r, 'No se pudo actualizar.');},
      error: (err:any) => this.error=this.errorAmigable(err, 'editar')
    }));
  }
  async borrar(f: FotoPrivada): Promise<void> {
    const resp = await Swal.fire({ title:'¿Eliminar esta fotografía?', text:'No podrás recuperarla.', icon:'warning',
      showCancelButton:true, confirmButtonText:'Eliminar', cancelButtonText:'Cancelar',
      confirmButtonColor:'#b64c48' });
    if (!resp.isConfirmed) return;
    this.subs.add(this.query.query('privadas','BORRAR',{ft_id:f.ft_id}).subscribe({
      next:(r:any)=> {if(r?.resultado==='OK') { if(this.seleccionada?.ft_id===f.ft_id) this.cerrar(); this.cargarFotos(); }
        else this.error=this.mensajePHP(r, 'No se pudo borrar.');},
      error:(err:any)=>this.error=this.errorAmigable(err, 'eliminar')
    }));
  }
  mover(event: CdkDragDrop<FotoPrivada[]>): void {
    if (this.filtro.trim() || event.previousIndex === event.currentIndex || this.ordenando) return;
    moveItemInArray(this.fotos,event.previousIndex,event.currentIndex);
    this.ordenando=true;
    this.subs.add(this.query.query('privadas','ORDENAR',{ids:this.fotos.map(f=>f.ft_id)}).subscribe({
      next:(r:any)=> { if(r?.resultado!=='OK') {this.error=this.mensajePHP(r, 'No se pudo ordenar.');this.cargarFotos(true);} this.ordenando=false; },
      error:(err:any)=> {this.error=this.errorAmigable(err, 'ordenar');this.ordenando=false;this.cargarFotos(true);}
    }));
  }
  trackFoto(_i:number,f:FotoPrivada):number {return f.ft_id;}
  private limpiarMemoria():void {for(const u of this.urls) URL.revokeObjectURL(u);this.urls=[];}
  ngOnDestroy():void { this.subs.unsubscribe();this.limpiarMemoria(); }
}
